/**
 * quark-teaser-panel.js
 *
 * Two custom elements:
 *   <quark-teaser-modal>  — glass morphism modal, manages its own open/close lifecycle
 *   <quark-teaser-panel>  — root section element, wires the email form to the modal
 *
 * Klaviyo integration:
 *   Submits via Klaviyo's client-side v3 subscriptions API using the public API key.
 *   No private key is exposed. The public key and list ID are injected from section settings.
 */

/* ============================================================
   KLAVIYO SUBSCRIPTION HELPER
   ============================================================ */

async function subscribeToKlaviyo({ email, firstName, lastName, birthdate, publicKey, listId }) {
  const response = await fetch(
    `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(publicKey)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-10-15'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  first_name: firstName,
                  last_name: lastName,
                  ...(birthdate && {
                    properties: {
                      'What is your baby\'s birthday?': birthdate
                    }
                  })
                }
              }
            }
          },
          // list_id must be in relationships, not attributes (rev 2024-10-15)
          relationships: {
            list: {
              data: { type: 'list', id: listId }
            }
          }
        }
      })
    }
  );

  // Klaviyo returns 202 Accepted on success (empty body)
  if (response.status === 202) return true;

  // Log the error body for debugging
  if (!response.ok) {
    try {
      const errBody = await response.clone().json();
      console.error('[quark-teaser-panel] Klaviyo error', response.status, JSON.stringify(errBody));
    } catch {
      console.error('[quark-teaser-panel] Klaviyo error', response.status);
    }
    return false;
  }

  return true;
}

/* ============================================================
   QUARK TEASER MODAL
   ============================================================ */

class QuarkTeaserModal extends HTMLElement {
  #previousFocus = null;
  #boundKeydown = null;

  connectedCallback() {
    this.#boundKeydown = this.#onKeydown.bind(this);

    this.querySelector('.qtp__modal-close')
      ?.addEventListener('click', () => this.hide());

    this.querySelector('.qtp__modal-success-close')
      ?.addEventListener('click', () => this.hide());

    this.querySelector('.qtp__modal-backdrop')
      ?.addEventListener('click', () => this.hide());

    this.querySelector('.qtp__modal-form')
      ?.addEventListener('submit', (e) => this.#onSubmit(e));
  }

  get open() {
    return this.hasAttribute('open');
  }

  /**
   * Show the modal, carrying the email from step 1.
   * @param {string} email
   */
  show(email) {
    // Carry the email from step 1 into the hidden field
    const carry = this.querySelector('.qtp-email-carry');
    if (carry) carry.value = email;

    // Reset any previous state
    this.#resetForm();

    // Store the element that triggered the modal so we can return focus on close
    this.#previousFocus = document.activeElement;

    // Remove inert, then RAF to allow browser to register the display change
    // before adding [open] so the CSS transition fires correctly
    this.removeAttribute('inert');

    requestAnimationFrame(() => {
      this.setAttribute('open', '');
      document.addEventListener('keydown', this.#boundKeydown);
      this.#focusFirstInput();
    });
  }

  hide() {
    this.removeAttribute('open');
    document.removeEventListener('keydown', this.#boundKeydown);

    this.addEventListener(
      'transitionend',
      () => {
        this.setAttribute('inert', '');
        this.#resetForm();
      },
      { once: true }
    );

    // Restore focus
    if (this.#previousFocus && typeof this.#previousFocus.focus === 'function') {
      this.#previousFocus.focus();
    }
  }

  /* ---- Private helpers ---- */

  #onKeydown(e) {
    if (e.key === 'Escape') {
      this.hide();
      return;
    }
    if (e.key === 'Tab') {
      this.#trapFocus(e);
    }
  }

  #trapFocus(e) {
    const focusable = Array.from(
      this.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.closest('[inert]') && el.offsetParent !== null);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  #focusFirstInput() {
    const first = this.querySelector(
      'input:not([type="hidden"]):not([disabled]), button:not([disabled])'
    );
    first?.focus();
  }

  #resetForm() {
    const form = this.querySelector('.qtp__modal-form');
    const success = this.querySelector('.qtp__modal-success');
    const errorEl = this.querySelector('.qtp__modal-error');
    const submitBtn = this.querySelector('.qtp__modal-submit');

    if (form) {
      form.reset();
      form.hidden = false;
    }
    if (success) success.hidden = true;
    if (errorEl) errorEl.removeAttribute('visible');
    if (submitBtn) submitBtn.disabled = false;
  }

  async #onSubmit(e) {
    e.preventDefault();

    const form = e.target;
    const submitBtn = this.querySelector('.qtp__modal-submit');
    const errorEl = this.querySelector('.qtp__modal-error');
    const successEl = this.querySelector('.qtp__modal-success');

    // Collect values
    const email = this.querySelector('.qtp-email-carry')?.value?.trim() || '';
    const firstName = form.querySelector('[name="first_name"]')?.value?.trim() || '';
    const lastName = form.querySelector('[name="last_name"]')?.value?.trim() || '';
    const birthdate = form.querySelector('[name="baby_birthdate"]')?.value || '';

    // Validate required fields
    if (!firstName) {
      this.#showError(errorEl, 'Please enter your first name.');
      form.querySelector('[name="first_name"]')?.focus();
      return;
    }
    if (!lastName) {
      this.#showError(errorEl, 'Please enter your last name.');
      form.querySelector('[name="last_name"]')?.focus();
      return;
    }
    if (!birthdate) {
      this.#showError(errorEl, 'Please enter your baby\'s birthday or due date.');
      form.querySelector('[name="baby_birthdate"]')?.focus();
      return;
    }

    if (errorEl) errorEl.removeAttribute('visible');

    // Read Klaviyo config from the root panel element
    const panel = this.closest('quark-teaser-panel');
    const publicKey = panel?.dataset.klaviyoKey || '';
    const listId = panel?.dataset.klaviyoList || '';

    if (!publicKey || !listId) {
      this.#showError(errorEl, 'Klaviyo is not configured. Please contact the site administrator.');
      return;
    }

    // Disable submit while in flight
    if (submitBtn) submitBtn.disabled = true;

    try {
      const success = await subscribeToKlaviyo({
        email,
        firstName,
        lastName,
        birthdate,
        publicKey,
        listId
      });

      if (success) {
        form.hidden = true;
        if (successEl) successEl.hidden = false;

        // Announce to screen readers
        const announcement = document.getElementById('status-announcement');
        if (announcement) {
          announcement.textContent = 'You have been successfully added to our list.';
        }
      } else {
        this.#showError(errorEl, 'Something went wrong. Please try again.');
        if (submitBtn) submitBtn.disabled = false;
      }
    } catch (err) {
      console.error('[quark-teaser-panel] Klaviyo subscription error:', err);
      this.#showError(errorEl, 'Something went wrong. Please try again.');
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  #showError(el, message) {
    if (!el) return;
    el.textContent = message;
    el.setAttribute('visible', '');
  }
}

/* ============================================================
   QUARK TEASER PANEL
   ============================================================ */

class QuarkTeaserPanel extends HTMLElement {
  connectedCallback() {
    this.querySelector('.qtp__email-form')
      ?.addEventListener('submit', (e) => this.#onEmailSubmit(e));
  }

  #onEmailSubmit(e) {
    e.preventDefault();

    const input = this.querySelector('.qtp__email-input');
    const errorEl = this.querySelector('.qtp__email-error');
    const email = input?.value?.trim() || '';

    if (!this.#isValidEmail(email)) {
      if (errorEl) {
        errorEl.textContent = 'Please enter a valid email address.';
        errorEl.setAttribute('visible', '');
      }
      input?.focus();
      return;
    }

    if (errorEl) errorEl.removeAttribute('visible');

    const modal = this.querySelector('quark-teaser-modal');
    modal?.show(email);
  }

  #isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }
}

/* ============================================================
   REGISTER CUSTOM ELEMENTS
   Guard with customElements.get() so Theme Editor re-injection
   doesn't throw "already defined" errors.
   ============================================================ */

if (!customElements.get('quark-teaser-modal')) {
  customElements.define('quark-teaser-modal', QuarkTeaserModal);
}

if (!customElements.get('quark-teaser-panel')) {
  customElements.define('quark-teaser-panel', QuarkTeaserPanel);
}
